import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Accordion from '@givebutter/primitives/accordion';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useMediaQuery } from '@givebutter/react-util';
import {
    faBookOpenReader,
    faCalendar,
    faCircleInfo,
    faMoneyBill,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    CreateCampaignDialogForm,
    campaignTypePosthog,
    useCreateCampaignDialogContext,
} from 'components/create-campaign-dialog/CreateCampaignDialogContext';
import * as SelectType from 'components/create-campaign-dialog/SelectTypeAccordion';
import {
    EVENT_VIDEO_DESKTOP,
    EVENT_VIDEO_MOBILE,
    FORM_VIDEO_DESKTOP,
    FORM_VIDEO_MOBILE,
    PAGE_VIDEO_DESKTOP,
    PAGE_VIDEO_MOBILE,
} from 'components/create-campaign-dialog/constants';
import { UseFormRadioGroup, useFormContext } from 'components/form';
import { posthogEvents } from 'constants/posthog-events';
import { usePosthog } from 'customHooks/usePosthog';
import { twMerge } from 'tailwind-merge';

const CampaignSelectType = () => {
    const { setStep } = useCreateCampaignDialogContext();
    const { control, path, setValue, watch } = useFormContext<CreateCampaignDialogForm>();
    const [videoLoaded, setVideoLoaded] = useState(false);
    const isMobile = useMediaQuery(`(max-width: 640px)`);

    const { pushEvent } = usePosthog();

    const eventType = watch('type');

    const videoSrc = useMemo(() => {
        switch (eventType) {
            case 'collect':
                return isMobile ? FORM_VIDEO_MOBILE : FORM_VIDEO_DESKTOP;
            case 'fundraise':
                return isMobile ? PAGE_VIDEO_MOBILE : PAGE_VIDEO_DESKTOP;
            case 'event':
            default:
                return isMobile ? EVENT_VIDEO_MOBILE : EVENT_VIDEO_DESKTOP;
        }
    }, [eventType, isMobile]);

    const manualSubmit = useCallback(() => {
        if (eventType) {
            pushEvent(posthogEvents.CAMPAIGN_CREATE_TYPE_SUBMITTED, {
                type: campaignTypePosthog[eventType],
            });
            setStep(eventType === 'collect' ? 'newTitle' : 'addFeatures');
        }
    }, [eventType, pushEvent, setStep]);

    useEffect(() => {
        setVideoLoaded(false);
    }, [eventType]);

    return (
        <div className="flex h-auto min-h-[100dvh] w-full min-w-fit flex-col-reverse items-center justify-center gap-4 p-4 sm:h-full sm:min-h-[100%] sm:flex-row sm:justify-start lg:gap-0">
            <div className="flex h-fit items-center justify-center sm:h-full sm:shrink-0 md:w-[45%] lg:w-1/2">
                <div className="flex w-full flex-col items-center justify-center gap-4 sm:w-80">
                    <h3 className={'mr-auto text-xl font-medium'}>Select a campaign type</h3>

                    <Accordion.Root
                        type="single"
                        value={eventType}
                        className={twMerge('flex flex-col gap-2')}
                    >
                        <UseFormRadioGroup
                            name={path('type')}
                            control={control}
                            orientation={'vertical'}
                            className={'flex flex-col gap-2'}
                            rules={{
                                required: 'Campaign type is required',
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    manualSubmit();
                                }
                            }}
                            onValueChange={value => {
                                if (value === 'collect') {
                                    setValue('enableTeamFundraising', false);
                                    setValue('enableAuction', false);
                                }
                            }}
                        >
                            <SelectType.Root type="collect">
                                <SelectType.Trigger>
                                    <SelectType.TriggerIcon icon={faMoneyBill} />
                                    <SelectType.TriggerLabel>Donation form</SelectType.TriggerLabel>
                                </SelectType.Trigger>
                                <SelectType.Content>
                                    <SelectType.ContentDescription>
                                        The easiest way to collect one-time or recurring donations.
                                        Customize with suggested donation amounts, a goal bar, or a
                                        description. Embed on your website to collect donations
                                        year-round.
                                    </SelectType.ContentDescription>
                                    <SelectType.ContentButton
                                        onClick={() => {
                                            pushEvent(
                                                posthogEvents.CAMPAIGN_CREATE_TYPE_SUBMITTED,
                                                { type: campaignTypePosthog['collect'] },
                                            );
                                            setStep('newTitle');
                                        }}
                                    />
                                </SelectType.Content>
                            </SelectType.Root>

                            <SelectType.Root type="fundraise">
                                <SelectType.Trigger>
                                    <SelectType.TriggerIcon icon={faBookOpenReader} />
                                    <SelectType.TriggerLabel>
                                        Fundraising page
                                    </SelectType.TriggerLabel>
                                </SelectType.Trigger>
                                <SelectType.Content>
                                    <SelectType.ContentDescription>
                                        An engaging page to showcase your story, rally supporters,
                                        and track donations in real-time. Raise even more with
                                        peer-to-peer, matching gifts, and embedded videos. Perfect
                                        for everything from annual drives to one-off campaigns.
                                    </SelectType.ContentDescription>
                                    <SelectType.ContentButton
                                        onClick={() => {
                                            pushEvent(
                                                posthogEvents.CAMPAIGN_CREATE_TYPE_SUBMITTED,
                                                { type: campaignTypePosthog['fundraise'] },
                                            );
                                            setStep('addFeatures');
                                        }}
                                    />
                                </SelectType.Content>
                            </SelectType.Root>

                            <SelectType.Root type="event">
                                <SelectType.Trigger>
                                    <SelectType.TriggerIcon icon={faCalendar} />
                                    <SelectType.TriggerLabel>Event</SelectType.TriggerLabel>
                                </SelectType.Trigger>
                                <SelectType.Content>
                                    <SelectType.ContentDescription>
                                        Host an in-person, hybrid, or virtual event. Manage and sell
                                        tickets and ticket bundles, track registrants and attendees,
                                        and collect donations. Great for galas, auctions, golf
                                        tournaments, and more.
                                    </SelectType.ContentDescription>
                                    <SelectType.ContentButton
                                        onClick={() => {
                                            pushEvent(
                                                posthogEvents.CAMPAIGN_CREATE_TYPE_SUBMITTED,
                                                { type: campaignTypePosthog['event'] },
                                            );
                                            setStep('addFeatures');
                                        }}
                                    />
                                </SelectType.Content>
                            </SelectType.Root>

                            <Button
                                variant={'solid'}
                                className={'sr-only'}
                                tabIndex={-1}
                                onClick={manualSubmit}
                            >
                                Get Started
                            </Button>
                        </UseFormRadioGroup>
                    </Accordion.Root>

                    <Button
                        type="button"
                        variant="solid"
                        color="neutral"
                        className="w-fit rounded-full text-sm text-gray-500"
                        onClick={() => {
                            pushEvent(posthogEvents.CAMPAIGN_CREATE_TYPE_COMPARISON_SELECTED);
                            setStep('info');
                        }}
                    >
                        <FontAwesomeIcon icon={faCircleInfo} />
                        Compare campaign types
                    </Button>
                </div>
            </div>

            <div
                aria-hidden
                className="relative flex h-56 w-full justify-center overflow-hidden rounded-xl bg-gray-50 sm:h-full sm:max-h-none sm:shrink-0 sm:justify-end sm:bg-transparent md:w-[55%] lg:w-1/2"
            >
                {!videoLoaded && <Loader />}
                <video
                    playsInline
                    controlsList="nofullscreen"
                    className="absolute h-full sm:rounded-xl"
                    src={videoSrc}
                    onLoadedData={() => {
                        setVideoLoaded(true);
                    }}
                    autoPlay
                    muted
                    loop
                />
            </div>
        </div>
    );
};

export { CampaignSelectType };
